import {
  CHAT_BUBBLE_ICON,
  CHAT_BUBBLE_ICON_GLYPH_SIZE_PX,
  IFRAME_OFFSET_PX,
  LAUNCHER_BUTTON_ELEMENT_ID,
  LAUNCHER_BUTTON_ICON_SIZE_PX,
  LAUNCHER_BUTTON_LABEL_TEXT,
  LAUNCHER_BUTTON_SHAPES,
  WEBCHAT_POSITIONS,
} from './constants';
import type { LauncherButtonShape, WebchatPosition } from './types';

// review: AMattRiley -- one generic validator instead of isWebchatPosition/isLauncherButtonShape
// each reimplementing the same "is this string one of the allowed values" body.
function includesValue<T extends string>(
  values: readonly T[],
  value: string | undefined,
): value is T {
  return (values as readonly string[]).includes(value ?? '');
}

export function isWebchatPosition(value: string | undefined): value is WebchatPosition {
  return includesValue(WEBCHAT_POSITIONS, value);
}

export function isLauncherButtonShape(value: string | undefined): value is LauncherButtonShape {
  return includesValue(LAUNCHER_BUTTON_SHAPES, value);
}

// SETO-7699: always anchored to the bottom -- data-webchat-position only chooses left vs. right.
const CORNER_STYLE: Record<WebchatPosition, string> = {
  right: `bottom:${IFRAME_OFFSET_PX}px;right:${IFRAME_OFFSET_PX}px;`,
  left: `bottom:${IFRAME_OFFSET_PX}px;left:${IFRAME_OFFSET_PX}px;`,
};

// review: AMattRiley -- transition softens the placeholder-to-brand-color swap (guaranteed on
// almost every load, given the SSR + config round trip inside the iframe) from a hard pop into a
// fade. Shared by both shapes so neither has to repeat it.
const BASE_BUTTON_STYLE =
  'z-index:2147483647;border:0;cursor:pointer;box-shadow:0 2px 8px rgba(26,26,26,0.16);' +
  'transition:background-color .15s ease,color .15s ease;';

const LABEL_SHAPE_STYLE =
  "border-radius:9999px;padding:12px 20px;font:600 15px/1.4 'Nunito Sans',system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;";

const ICON_SHAPE_STYLE =
  `width:${LAUNCHER_BUTTON_ICON_SIZE_PX}px;height:${LAUNCHER_BUTTON_ICON_SIZE_PX}px;` +
  'padding:0;border-radius:50%;display:flex;align-items:center;justify-content:center;';

// review: AMattRiley -- parallel to CORNER_STYLE above, so createLauncherButton below builds its
// cssText from one shared expression instead of re-deriving the position:fixed/CORNER_STYLE/
// BASE_BUTTON_STYLE prefix in both branches of an if/else.
const SHAPE_STYLE: Record<LauncherButtonShape, string> = {
  label: LABEL_SHAPE_STYLE,
  icon: ICON_SHAPE_STYLE,
};

// SETO-7701: shown until webchat:ready's resolved headerColors arrives -- picking one consistent
// placeholder rather than leaving the button uncolored (transparent/invisible) in the meantime.
const PLACEHOLDER_BACKGROUND = '#1A1A1A';
const PLACEHOLDER_FOREGROUND = '#FFFFFF';

const SVG_NS = 'http://www.w3.org/2000/svg';

// Plain speech-bubble silhouette (rounded rect + a small pointed tail), built via DOM/SVG APIs
// rather than innerHTML (this script runs on arbitrary third-party pages). fill="currentColor" so
// it always tracks the button's own `color` -- no separate color-update path needed when
// setLauncherButtonColors runs later.
function createChatBubbleIcon(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', CHAT_BUBBLE_ICON.viewBox);
  svg.setAttribute('width', String(CHAT_BUBBLE_ICON_GLYPH_SIZE_PX));
  svg.setAttribute('height', String(CHAT_BUBBLE_ICON_GLYPH_SIZE_PX));
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const bubble = document.createElementNS(SVG_NS, 'rect');
  bubble.setAttribute('x', String(CHAT_BUBBLE_ICON.body.x));
  bubble.setAttribute('y', String(CHAT_BUBBLE_ICON.body.y));
  bubble.setAttribute('width', String(CHAT_BUBBLE_ICON.body.width));
  bubble.setAttribute('height', String(CHAT_BUBBLE_ICON.body.height));
  bubble.setAttribute('rx', String(CHAT_BUBBLE_ICON.body.rx));
  bubble.setAttribute('fill', 'currentColor');

  const tail = document.createElementNS(SVG_NS, 'path');
  tail.setAttribute('d', CHAT_BUBBLE_ICON.tailPath);
  tail.setAttribute('fill', 'currentColor');

  svg.append(bubble, tail);
  return svg;
}

// A real <button> (not a styled <div>) -- keyboard-reachable via Tab and given a visible focus
// state for free by not touching outline. Positioned independently of the iframe/fallback
// (host-page chrome, always document.body, regardless of options.staticContainerId -- that escape
// hatch is about where the *iframe* mounts, not this).
//
// SETO-7699 (icon-bubble addendum): 'label' keeps its text content as its accessible name (as
// before); 'icon' has no visible text, so aria-label carries the same "Chat with us" name instead
// -- both shapes end up with an identical accessible name, just via a different mechanism.
export function createLauncherButton(
  position: WebchatPosition,
  shape: LauncherButtonShape,
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.id = LAUNCHER_BUTTON_ELEMENT_ID;

  if (shape === 'icon') {
    button.setAttribute('aria-label', LAUNCHER_BUTTON_LABEL_TEXT);
    button.appendChild(createChatBubbleIcon());
  } else {
    button.textContent = LAUNCHER_BUTTON_LABEL_TEXT;
  }
  button.style.cssText = `position:fixed;${CORNER_STYLE[position]}${BASE_BUTTON_STYLE}${SHAPE_STYLE[shape]}`;

  // review: claude -- hideLauncherButton overwrites the display set above (e.g. icon shape's
  // 'flex', needed for its centering) with 'none'; showLauncherButton can't just reset to '' to
  // undo that, since that falls through to <button>'s UA-default 'inline-block' rather than back
  // to 'flex'. Reading the real value back off style.display here (right after cssText set it) is
  // the single source of truth for what "shown" means for this button, so it can't drift from
  // whatever ICON_SHAPE_STYLE/LABEL_SHAPE_STYLE actually declare.
  button.dataset.shownDisplay = button.style.display;

  setLauncherButtonColors(button, PLACEHOLDER_BACKGROUND, PLACEHOLDER_FOREGROUND);
  return button;
}

// SETO-7701: applied once the resolved theme arrives on webchat:ready -- no independent hex/
// luminance computation here, web-chat-ui already resolved a readable foreground for this
// background (resolveTheme.ts's headerColors, resolved client-side there against the visitor's
// prefers-color-scheme so it matches whatever mode the widget's own iframe is rendering in --
// not something this loader could reproduce itself, since it has no visibility into that).
export function setLauncherButtonColors(
  button: HTMLButtonElement,
  background: string,
  foreground: string,
): void {
  button.style.background = background;
  button.style.color = foreground;
}

// Hidden (not removed) while the widget is open, so it never sits behind/beside the open iframe
// or fallback at the same corner; restored on close. Mirrors the iframe/fallback's own show/hide
// pattern (iframe.ts) rather than mounting/unmounting the element.
export function showLauncherButton(button: HTMLButtonElement): void {
  button.style.display = button.dataset.shownDisplay ?? '';
}

export function hideLauncherButton(button: HTMLButtonElement): void {
  button.style.display = 'none';
}

// Same DOMContentLoaded deferral as attachIframe (iframe.ts) -- a script tag can run in <head>
// before document.body exists.
export function attachLauncherButton(button: HTMLButtonElement): void {
  const attach = (): void => {
    if (document.getElementById(LAUNCHER_BUTTON_ELEMENT_ID)) return;
    document.body.appendChild(button);
  };

  if (document.body && document.readyState !== 'loading') {
    attach();
  } else {
    document.addEventListener('DOMContentLoaded', attach);
  }
}
